import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { RoofFace, RoofFeature } from '../../types';
import { api } from '../../lib/api';

// Roof Face Hooks

// Hook to fetch roof faces for a measurement
export function useRoofFaces(measurementId?: number) {
  return useQuery({
    queryKey: ['roof-faces', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/roof-faces/by_measurement/?measurement_id=${measurementId}`);
      return response.data as RoofFace[];
    },
    enabled: !!measurementId,
  });
}

// Hook to create a roof face
export function useCreateRoofFace() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Omit<RoofFace, 'id' | 'created_at' | 'updated_at'>) => {
      const response = await api.post('/api/roof-faces/', data);
      return response.data as RoofFace;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
      queryClient.invalidateQueries({ queryKey: ['roof-sections', data.roof_section] });
    },
  });
}

// Hook to update a roof face
export function useUpdateRoofFace() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RoofFace> }) => {
      const response = await api.patch(`/api/roof-faces/${id}/`, data);
      return response.data as RoofFace;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
      queryClient.invalidateQueries({ queryKey: ['roof-face', data.id] });
    },
  });
}

// Hook to update pitch for a roof face
export function useUpdateRoofFacePitch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, pitch_ratio, pitch_degrees }: { id: number; pitch_ratio?: string; pitch_degrees?: number }) => {
      const response = await api.post(`/api/roof-faces/${id}/update_pitch/`, {
        pitch_ratio,
        pitch_degrees,
      });
      return response.data as RoofFace;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
      queryClient.invalidateQueries({ queryKey: ['roof-face', data.id] });
    },
  });
}

// Hook to delete a roof face
export function useDeleteRoofFace() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/roof-faces/${id}/`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
    },
  });
}

// Roof Feature Hooks

// Hook to fetch roof features for a measurement
export function useRoofFeatures(measurementId?: number) {
  return useQuery({
    queryKey: ['roof-features', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/roof-features/by_measurement/?measurement_id=${measurementId}`);
      return response.data as RoofFeature[];
    },
    enabled: !!measurementId,
  });
}

// Hook to fetch feature summary by type
export function useRoofFeatureSummary(measurementId?: number) {
  return useQuery({
    queryKey: ['roof-feature-summary', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/roof-features/summary_by_type/?measurement_id=${measurementId}`);
      return response.data;
    },
    enabled: !!measurementId,
  });
}

// Hook to create a roof feature
export function useCreateRoofFeature() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Omit<RoofFeature, 'id' | 'created_at' | 'updated_at'>) => {
      const response = await api.post('/api/roof-features/', data);
      return response.data as RoofFeature;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary', data.measurement] });
    },
  });
}

// Hook to update a roof feature
export function useUpdateRoofFeature() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RoofFeature> }) => {
      const response = await api.patch(`/api/roof-features/${id}/`, data);
      return response.data as RoofFeature;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature', data.id] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary', data.measurement] });
    },
  });
}

// Hook to delete a roof feature
export function useDeleteRoofFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/roof-features/${id}/`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary'] });
    },
  });
}

// Hook to bulk create roof features
export function useBulkCreateRoofFeatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { features: Omit<RoofFeature, 'id' | 'created_at' | 'updated_at'>[] }) => {
      const response = await api.post('/api/roof-features/bulk-create/', data);
      return response.data as RoofFeature[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary'] });
    },
  });
}

// Hook to sync linear totals to DetailedMeasurement
export function useSyncLinearTotals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { measurementId: number; pitch?: string; wasteFactor?: number }) => {
      const response = await api.post('/api/roof-features/sync-linear-totals/', {
        measurement_id: params.measurementId,
        predominant_pitch: params.pitch,
        waste_factor: params.wasteFactor,
      });
      return response.data as { id: number; [key: string]: unknown };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['detailed-measurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurementsByOpportunity'] });
    },
  });
}

// Hook to generate a PDF report from a DetailedMeasurement
export function useGenerateReportFromMeasurement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (detailedMeasurementId: number) => {
      const { authService } = await import('../../lib/auth');
      const token = authService.getAccessToken();
      const resp = await fetch('/api/integrations/roof-measurement/reports/generate-from-measurement/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ detailed_measurement_id: detailedMeasurementId }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      return resp.blob();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunityMeasurementDocs'] });
      queryClient.invalidateQueries({ queryKey: ['docs'] });
    },
  });
}